import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Cake, Heart, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Field, Modal, PageHead, WaButton, inputCls, itDate, saveDoc, useList } from "@/components/kit";
import { api } from "@/lib/api";

const TIPI = ["Compleanno", "Anniversario di matrimonio", "Onomastico", "Anniversario aziendale", "Altro"];

function ContactForm({ c, onDone }) {
  const [f, setF] = useState({ nome: "", tel: "", email: "", note: "", ricorrenze: [], ...c });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const setR = (i, k, v) => setF({ ...f, ricorrenze: f.ricorrenze.map((r, j) => (j === i ? { ...r, [k]: v } : r)) });
  const save = async () => {
    const body = { nome: f.nome, tel: f.tel, email: f.email, note: f.note, ricorrenze: f.ricorrenze.filter((r) => r.data).map(({ tipo, data, persona }) => ({ tipo, data, persona })) };
    if (await saveDoc("/contacts", f.id, body)) onDone();
  };
  return (
    <div className="space-y-3">
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Nome *"><input className={inputCls} value={f.nome} onChange={set("nome")} data-testid="contact-name" /></Field>
        <Field label="Telefono"><input className={inputCls} value={f.tel} onChange={set("tel")} data-testid="contact-phone" /></Field>
        <Field label="E-mail"><input className={inputCls} value={f.email} onChange={set("email")} data-testid="contact-email" /></Field>
        <Field label="Note"><input className={inputCls} value={f.note} onChange={set("note")} data-testid="contact-note" /></Field>
      </div>
      <div className="text-xs font-medium text-[#47606B]">Date da ricordare</div>
      {f.ricorrenze.map((r, i) => (
        <div key={i} className="grid grid-cols-12 gap-2">
          <select className={`${inputCls} col-span-4`} value={r.tipo} onChange={(e) => setR(i, "tipo", e.target.value)} data-testid={`contact-ric-type-${i}`}>{TIPI.map((t) => <option key={t}>{t}</option>)}</select>
          <input type="date" className={`${inputCls} col-span-3`} value={r.data} onChange={(e) => setR(i, "data", e.target.value)} data-testid={`contact-ric-date-${i}`} />
          <input placeholder="Persona" className={`${inputCls} col-span-4`} value={r.persona || ""} onChange={(e) => setR(i, "persona", e.target.value)} data-testid={`contact-ric-person-${i}`} />
          <button className="col-span-1 text-[#991B1B]" onClick={() => setF({ ...f, ricorrenze: f.ricorrenze.filter((_, j) => j !== i) })} data-testid={`contact-ric-remove-${i}`}><Trash2 size={14} /></button>
        </div>
      ))}
      <Button size="sm" variant="outline" className="rounded-full" onClick={() => setF({ ...f, ricorrenze: [...f.ricorrenze, { tipo: "Compleanno", data: "", persona: "" }] })} data-testid="contact-add-ric"><Plus size={13} className="mr-1" /> Aggiungi data</Button>
      <div><Button className="rounded-full" onClick={save} disabled={!f.nome.trim()} data-testid="contact-save">Salva cliente</Button></div>
    </div>
  );
}

function Upcoming({ rows }) {
  const all = rows.flatMap((c) => (c.ricorrenze || []).filter((r) => r.prossima).map((r) => ({ ...r, c }))).sort((a, b) => a.prossima.localeCompare(b.prossima)).slice(0, 8);
  if (!all.length) return null;
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="upcoming-dates">
      {all.map((r, i) => {
        const Icon = r.tipo === "Compleanno" ? Cake : Heart;
        return (
          <div key={i} className="rounded-xl border border-[#D4E6EC] bg-white p-4 fade-up" style={{ animationDelay: `${i * 50}ms` }}>
            <div className="flex items-center gap-2 text-[#0B6E8F] text-xs uppercase tracking-wider"><Icon size={14} /> {itDate(r.prossima)}</div>
            <div className="font-medium mt-1">{r.persona || r.c.nome}</div>
            <div className="text-xs text-[#7B929C]">{r.tipo}{r.anni ? ` • ${r.anni}°` : ""}</div>
          </div>
        );
      })}
    </div>
  );
}

export default function Clients() {
  const [rows, reload] = useList("/contacts");
  const [edit, setEdit] = useState(null);
  const [params, setParams] = useSearchParams();
  useEffect(() => {
    if (params.get("new")) {
      setEdit({});
      setParams({});
    }
  }, [params, setParams]);
  const [q, setQ] = useState("");
  const list = useMemo(() => (rows || []).filter((c) => !q || `${c.nome} ${c.tel}`.toLowerCase().includes(q.toLowerCase())), [rows, q]);
  const remove = async () => {
    await api.delete(`/contacts/${edit.id}`);
    setEdit(null);
    reload();
  };
  return (
    <div className="space-y-6">
      <PageHead eyebrow="Compleanni, anniversari e ricorrenze" title="Clienti">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca cliente…" className={`${inputCls} rounded-full w-48`} data-testid="clients-search" />
        <Button className="rounded-full" onClick={() => setEdit({})} data-testid="new-contact-button"><Plus size={15} className="mr-2" /> Nuovo cliente</Button>
      </PageHead>
      <p className="text-sm text-[#47606B] max-w-3xl">Le date arrivano da sole dai contratti: compleanni letti dai documenti d'identità, anniversari di matrimonio e degli eventi. Potete aggiungere clienti e date a mano. Ogni ricorrenza compare in «Oggi» con il messaggio di auguri pronto da inviare.</p>
      {rows && <Upcoming rows={rows} />}
      <div className="rounded-xl border border-[#D4E6EC] bg-white overflow-x-auto">
        <table className="w-full text-sm" data-testid="clients-table">
          <thead className="text-left text-[11px] uppercase tracking-wider text-[#7B929C] border-b border-[#D4E6EC]"><tr><th className="p-4">Cliente</th><th className="p-4">Date da ricordare</th><th className="p-4">Origine</th><th className="p-4" /></tr></thead>
          <tbody>
            {rows && list.length === 0 && <tr><td colSpan={4} className="p-8 text-center text-[#7B929C]" data-testid="clients-empty">Nessun cliente ancora.</td></tr>}
            {list.map((c) => (
              <tr key={c.id} className="border-b border-[#EAF5F7]" data-testid={`client-row-${c.id}`}>
                <td className="p-4"><Link to={`/clienti/scheda?nome=${encodeURIComponent(c.nome)}&tel=${encodeURIComponent(c.tel || "")}`} className="font-medium text-[#0B6E8F] underline decoration-[#34B3B0]/50 underline-offset-4 hover:decoration-[#0B6E8F]" data-testid={`client-profile-link-${c.id}`}>{c.nome}</Link><div className="text-xs text-[#7B929C]">{c.tel} {c.email}</div></td>
                <td className="p-4 text-xs space-y-0.5">{(c.ricorrenze || []).map((r, i) => <div key={i}>{r.tipo}: <b>{itDate(r.data)}</b>{r.persona ? ` • ${r.persona}` : ""} <span className="text-[#7B929C]">(prossimo {itDate(r.prossima)})</span></div>)}</td>
                <td className="p-4 text-xs">{c.source === "contratto" ? <Link className="text-[#0B6E8F] hover:underline" to={`/contratti/${c.contract_id}`}>Contratto {c.contract_number}</Link> : "Inserito a mano"}</td>
                <td className="p-4"><div className="flex gap-2 justify-end">
                  <WaButton phone={c.tel} testId={`client-wa-${c.id}`} text={`🌟 Gentile *${c.nome}*,\n\n✨ un saluto affettuoso da tutto lo staff! Speriamo di rivederla presto per una nuova occasione speciale. 🥂\n\nUn caro saluto 🌊`} />
                  {c.source === "manuale" && <Button size="sm" variant="outline" className="rounded-full" onClick={() => setEdit(c)} data-testid={`client-edit-${c.id}`}>Modifica</Button>}
                </div></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Modal open={!!edit} onOpenChange={(o) => !o && setEdit(null)} title={edit?.id ? edit.nome : "Nuovo cliente"} testId="contact-dialog">
        {edit && <ContactForm c={edit} onDone={() => { setEdit(null); reload(); }} />}
        {edit?.id && <Button variant="ghost" className="rounded-full text-[#991B1B]" onClick={remove} data-testid="contact-delete"><Trash2 size={14} className="mr-1" /> Elimina</Button>}
      </Modal>
    </div>
  );
}
